import * as React from 'react';
import { focusManager } from 'react-query';

/**
 * How long a tab must go without a re-validation before returning to it is worth
 * another one. Auth lapses over hours, so a quick alt-tab has nothing to
 * discover — and on a slow Stardog every check is a visible stall.
 */
export const REVALIDATE_ON_RETURN_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Runs `onReturn` when the user comes back to the app — window focus, or the tab
 * becoming visible again — but at most once every
 * `REVALIDATE_ON_RETURN_INTERVAL_MS`, so flipping between tabs costs nothing.
 *
 * Subscribes to react-query's focus manager rather than adding our own listeners,
 * since it is already wired up for query refetching. The manager notifies
 * subscribers when the tab becomes *hidden* too, hence the `isFocused()` guard.
 *
 * Pass a stable `onReturn` (e.g. from `React.useCallback`) so the subscription
 * isn't torn down and rebuilt on every render, which would also restart the
 * interval.
 */
export const useRevalidateOnReturn = (onReturn: () => void) => {
  React.useEffect(() => {
    // Mounting counts as a run: the caller validates on mount, so a focus a
    // moment later has nothing new to check.
    let lastRunAt = Date.now();

    return focusManager.subscribe(() => {
      if (!focusManager.isFocused()) {
        return;
      }

      const now = Date.now();
      if (now - lastRunAt < REVALIDATE_ON_RETURN_INTERVAL_MS) {
        return;
      }

      lastRunAt = now;
      onReturn();
    });
  }, [onReturn]);
};
